import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { zVar } from "../lib/zIndex";
import { InfoTip } from "../components/InfoTip";

// ─── Daily Briefing Panel (v119.63) ──────────────────────────────────
function DailyBriefingPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // v119.103 — Cooldown awareness: poll status, disable button + show countdown.
  const [cooldown, setCooldown] = useState({ remaining_seconds: 0, last_by: "", last_at: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp/daily-briefing");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل الموجز");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // v119.103 — Pull cooldown state every 10s while button visible.
  const loadCooldown = useCallback(async () => {
    try {
      const r = await api.get("/whatsapp/cooldown-status");
      setCooldown(r.data?.daily_briefing || { remaining_seconds: 0 });
    } catch { /* non-critical */ }
  }, []);
  useEffect(() => {
    loadCooldown();
    const t = setInterval(loadCooldown, 10_000);
    return () => clearInterval(t);
  }, [loadCooldown]);

  // Local 1s countdown ticker so the displayed remaining time feels live.
  useEffect(() => {
    if (!cooldown.remaining_seconds) return undefined;
    const t = setInterval(() => {
      setCooldown((c) => ({ ...c, remaining_seconds: Math.max(0, (c.remaining_seconds || 0) - 1) }));
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooldown.remaining_seconds > 0]);

  const doSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const r = await api.post("/whatsapp/daily-briefing/send");
      toast.success(`✅ تم الإرسال إلى ${r.data?.sent || 0}/${r.data?.total || 0} رقم`);
      loadCooldown();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.remaining_seconds) {
        setCooldown({
          remaining_seconds: d.remaining_seconds,
          last_by: d.last_by, last_at: d.last_at,
        });
        toast.error(d.message || "Cooldown نشط");
      } else {
        toast.error(d || "تعذّر الإرسال");
      }
    } finally { setSending(false); }
  };

  const sendNow = () => setConfirmOpen(true);

  if (loading) return <div className="card" style={{ padding: 20, textAlign: "center" }}>⏳ جارٍ التحميل...</div>;
  const b = data?.briefing || {};
  const y = b.yesterday || {};
  const s = b.sentiment_24h || {};
  return (
    <div data-testid="wa-briefing-panel">
      <div className="card" style={{ padding: 12, marginBottom: 10, background: "linear-gradient(135deg,#fef3c7 0%,#fde68a 100%)", border: "1px solid #f59e0b" }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#78350f", marginBottom: 4 }}>☀️ الموجز الذكي اليومي</div>
        <div style={{ fontSize: 11.5, color: "#78350f", lineHeight: 1.7 }}>
          يصلك تلقائياً كل صباح الساعة {data?.hour_ksa ?? 8} على رقم WhatsApp يحوي: مبيعات الأمس، شعور العملاء آخر 24 ساعة، وعدد الحملات الجاهزة للتنفيذ. أو اضغط الزر بالأسفل لإرساله الآن.
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div className="card" style={{ padding: 12 }} data-testid="wa-briefing-yesterday">
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>📊 الأمس</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <KPIBox label="💰 إيراد" value={`${(y.revenue || 0).toLocaleString()} ر`} />
            <KPIBox label="🧾 عمليات" value={y.transactions || 0} />
            <KPIBox label="📈 متوسط" value={`${y.atv || 0} ر`} />
          </div>
        </div>
        <div className="card" style={{ padding: 12 }} data-testid="wa-briefing-sentiment">
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>💬 آخر 24 ساعة</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <KPIBox label="📝 رسائل" value={s.total || 0} />
            <KPIBox label="😡 شكاوى" value={s.complaints || 0} color={s.complaints > 0 ? "#dc2626" : undefined} />
            <KPIBox label="🚨 عاجلة" value={s.high_urgency || 0} color={s.high_urgency > 0 ? "#dc2626" : undefined} />
          </div>
        </div>
      </div>
      {b.actionable_count > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 10, border: "1px solid #25D366" }} data-testid="wa-briefing-actionable">
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 6 }}>
            💡 لديك {b.actionable_count} حملة WhatsApp مقترحة جاهزة للتنفيذ
          </div>
          <ol style={{ margin: 0, paddingInlineStart: 20, fontSize: 12, lineHeight: 1.8 }}>
            {(b.actionable_suggestions || []).map((t, i) => <li key={i}>{t}</li>)}
          </ol>
        </div>
      )}
      <div className="card" style={{ padding: 12 }} data-testid="wa-briefing-rendered">
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 6 }}>📱 نص رسالة WhatsApp المُرسلة:</div>
        <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.8, padding: 10, background: "#f8f6f1", borderRadius: 6, margin: 0 }}>{data?.rendered_text || ""}</pre>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" onClick={load} className="btn btn-soft" style={{ fontSize: 12 }} data-testid="wa-briefing-refresh">
          🔄 تحديث
        </button>
        <button type="button" onClick={sendNow}
          disabled={sending || cooldown.remaining_seconds > 0}
          className="btn btn-dark"
          style={{
            fontSize: 12.5,
            background: cooldown.remaining_seconds > 0 ? "#9ca3af" : "#25D366",
            borderColor: cooldown.remaining_seconds > 0 ? "#9ca3af" : "#25D366",
            opacity: cooldown.remaining_seconds > 0 ? 0.7 : 1,
          }}
          data-testid="wa-briefing-send-now"
         aria-label={cooldown.remaining_seconds > 0
            ? `Cooldown نشط — انتظر ${Math.ceil(cooldown.remaining_seconds / 60)} دقيقة`
            : "إرسال الموجز اليومي لكل أرقام المدراء"}>
          {sending ? "⏳ يرسل..."
           : cooldown.remaining_seconds > 0
             ? `🔒 انتظر ${String(Math.floor(cooldown.remaining_seconds / 60)).padStart(2, "0")}:${String(cooldown.remaining_seconds % 60).padStart(2, "0")}`
             : "📤 أرسل الآن لكل المدراء"}
        </button>
        {cooldown.remaining_seconds > 0 && cooldown.last_by && (
          <span data-testid="wa-briefing-cooldown-note"
            style={{ fontSize: 11, color: "var(--muted)" }}>
            آخر إرسال من قِبل <strong>{cooldown.last_by}</strong>
            {cooldown.last_at ? ` · ${(cooldown.last_at || "").slice(11, 16)}` : ""}
          </span>
        )}
      </div>

      {/* v119.103 — Confirmation modal (replaces window.confirm). */}
      {confirmOpen && (
        <div data-testid="wa-briefing-confirm-modal"
          onClick={() => setConfirmOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: zVar('extra_9999'),
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, padding: 22, maxWidth: 440, width: "100%",
                     boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8 }}>
              📤 تأكيد إرسال الموجز اليومي
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.9, color: "var(--muted)", marginBottom: 14 }}>
              سيتم إرسال نص الموجز إلى <strong>كل أرقام المدراء</strong> على واتساب فوراً.
              <br />
              ⚠️ الـ Cooldown هو 30 دقيقة — لن تستطيع إرسال موجز ثانٍ خلال هذه المدة.
              <br />
              💡 الإرسال التلقائي اليومي يتم الساعة {data?.hour_ksa ?? 8} صباحاً — اضغط «إرسال» فقط إذا كنت تحتاج تنبيهاً عاجلاً.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setConfirmOpen(false)}
                data-testid="wa-briefing-confirm-cancel"
                style={{ padding: "8px 16px", background: "#fff", color: "var(--brown-deep)",
                         border: "1px solid var(--border)", borderRadius: 8, fontSize: 13,
                         fontWeight: 700, cursor: "pointer" }}>
                ✗ إلغاء
              </button>
              <button type="button" onClick={doSend}
                data-testid="wa-briefing-confirm-send"
                style={{ padding: "8px 16px", background: "#25D366", color: "#fff",
                         border: "none", borderRadius: 8, fontSize: 13,
                         fontWeight: 700, cursor: "pointer" }}>
                📤 أرسل الآن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPIBox({ label, value, color }) {
  return (
    <div style={{ background: "#f8f6f1", padding: "8px 6px", borderRadius: 6, textAlign: "center" }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: color || "var(--brown-deep)" }}>{value}</div>
    </div>
  );
}

export default DailyBriefingPanel;
